import { Disposable } from "@codesandbox/pitcher-client";
import { useEditor } from "features/editor/Feature";
import * as vscode from "vscode";
import { registerExtension } from "vscode/extensions";
import { DisposableStore } from "vscode/monaco";

import { useVSCodeExtensionInitializedEffect } from "hooks/useVSCodeExtensionInitializedEffect";
import { executeVSCodeCommand } from "utils/vscode";

import { useActiveEditorInput } from "./utils";

import { MARKDOWN_EDITOR_ID } from ".";

/**
 * Adds some buttons in VSCode to control the layout
 */
export function useLayoutButtons() {
  const editor = useEditor();

  useVSCodeExtensionInitializedEffect(() => {
    const disposableStore = new DisposableStore();
    const onExtHostInitialized = async () => {
      // Wait for other extensions to initialize first
      await new Promise((resolve) => setTimeout(resolve, 100));
      const { dispose, getApi } = registerExtension(
        {
          name: "codesandbox.editor.integration",
          version: "1.0.0",
          publisher: "CodeSandbox",
          engines: {
            vscode: "*",
          },
          contributes: {
            commands: [
              {
                command: "codesandbox.layout.toggleDevtools",
                title: `Toggle Devtools`,
                category: "Layout",
                // @ts-ignore
                icon: "$(layout-sidebar-right)",
              },
              {
                command: "codesandbox.layout.openDevtool",
                title: `Open New Devtool`,
                category: "Layout",
                // @ts-ignore
                icon: "$(plus)",
              },
              {
                command: "codesandbox.layout.showRawEditor",
                title: "Show Code Editor",
                category: "Layout",
                // @ts-ignore
                icon: "$(file-code)",
              },
              {
                command: "codesandbox.layout.showInteractiveMarkdown",
                title: "Show Interactive Markdown Preview",
                category: "Layout",
                // @ts-ignore
                icon: "$(preview)",
              },
              {
                command: "codesandbox.files.download",
                title: "Download",
                category: "Settings",
                // @ts-ignore
                icon: "$(cloud-download)",
              },
            ],
            menus: {
              "editor/title": [
                {
                  command: "codesandbox.layout.toggleDevtools",
                  group: "navigation",
                },
                {
                  command: "codesandbox.layout.openDevtool",
                  group: "navigation",
                  when: "resourceScheme == 'csb-tab'",
                },
                {
                  command: "codesandbox.layout.showRawEditor",
                  group: "navigation",
                  when: "codesandbox.layout.hasMarkdownPreviewOpened",
                },
                {
                  command: "codesandbox.layout.showInteractiveMarkdown",
                  group: "navigation",
                  when: "codesandbox.layout.hasMarkdownRawOpened",
                },
              ],
              "view/title": [
                {
                  command: "codesandbox.files.download",
                  group: "navigation",
                  when: "view == workbench.explorer.fileView && codesandbox.files.canDownload == true",
                },
              ],
            },

            keybindings: [
              {
                command: "codesandbox.layout.toggleDevtools",
                key: "ctrl+'",
                mac: "cmd+'",
              },
            ],
          },
        },
        1,
        { system: true },
      );

      getApi().then((api) => {
        disposableStore.add(
          api.commands.registerCommand(
            "codesandbox.layout.showRawEditor",
            () => {
              api.commands.executeCommand("workbench.action.reopenTextEditor");
            },
          ),
        );

        disposableStore.add(
          api.commands.registerCommand(
            "codesandbox.layout.showInteractiveMarkdown",
            () => {
              api.commands.executeCommand("workbench.action.toggleEditorType");
            },
          ),
        );
      });

      disposableStore.add(Disposable.create(dispose));

      const codelensProvider = vscode.languages.registerCodeLensProvider(
        { language: "dotenv" },
        {
          provideCodeLenses() {
            const topOfDocument = new vscode.Range(0, 0, 0, 0);

            const c: vscode.Command = {
              command: "codesandbox.settings.env",
              title:
                "For configuring secret environment variables, click here.",
            };

            const codeLens = new vscode.CodeLens(topOfDocument, c);

            return [codeLens];
          },
        },
      );

      disposableStore.add(codelensProvider);
    };

    onExtHostInitialized();

    return () => {
      disposableStore.dispose();
    };
  }, []);

  useVSCodeExtensionInitializedEffect(() => {
    vscode.commands.executeCommand(
      "setContext",
      "codesandbox.files.canDownload",
      editor.canDownload,
    );
  }, [editor.canDownload]);

  const activeEditor = useActiveEditorInput();

  useVSCodeExtensionInitializedEffect(() => {
    const setContext = (
      hasMarkdownPreviewOpened = false,
      hasMarkdownRawOpened = false,
    ) => {
      vscode.commands.executeCommand(
        "setContext",
        "codesandbox.layout.hasMarkdownPreviewOpened",
        hasMarkdownPreviewOpened,
      );
      vscode.commands.executeCommand(
        "setContext",
        "codesandbox.layout.hasMarkdownRawOpened",
        hasMarkdownRawOpened,
      );
    };
    if (!activeEditor) {
      setContext();
      return;
    }
    if (!("resource" in activeEditor) || !activeEditor.resource) {
      setContext();
      return;
    }

    if (!activeEditor.resource.path.toLowerCase().endsWith(".md")) {
      setContext();
      return;
    }

    if (activeEditor.editorId === MARKDOWN_EDITOR_ID) {
      setContext(true, false);
    } else {
      setContext(false, true);
    }
  }, [activeEditor]);

  // Ensure that if we open an empty markdown it's in edit mode immediately
  useVSCodeExtensionInitializedEffect(() => {
    if (!activeEditor) {
      return;
    }
    if (!("resource" in activeEditor) || !activeEditor.resource) {
      return;
    }

    if (!activeEditor.resource.path.toLowerCase().endsWith(".md")) {
      return;
    }

    (async () => {
      if (!activeEditor.resource) {
        return;
      }

      if (activeEditor.editorId === MARKDOWN_EDITOR_ID) {
        const model = await vscode.workspace.openTextDocument(
          activeEditor.resource,
        );
        if (model.getText().trim() === "") {
          executeVSCodeCommand("codesandbox.layout.showRawEditor");
        }
      }
    })();
  }, [activeEditor]);

  useVSCodeExtensionInitializedEffect(function customLayout() {
    // This hides the timeline panel in explorer as it is not useful
    // given its incorrect values, but still a required module (getTimelineServiceOverride)
    executeVSCodeCommand("timeline.removeView");
  }, []);
}
